import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { trackOnboardingEvent } from '../lib/analytics-events';

export function RegisterConfirmation() {
  const [params]   = useSearchParams();
  const navigate   = useNavigate();
  const email      = params.get('email') ?? '';

  const [resendCooldown, setResendCooldown] = useState(0);
  const [resending,      setResending]      = useState(false);
  const [resendOk,       setResendOk]       = useState(false);

  useEffect(() => {
    if (!email) { navigate('/register', { replace: true }); return; }

    // Poll for email confirmation every 3 seconds
    const interval = setInterval(async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email_confirmed_at) {
        trackOnboardingEvent('email_confirmed', { email });
        clearInterval(interval);
        navigate('/onboarding', { replace: true });
      }
    }, 3_000);

    return () => clearInterval(interval);
  }, [email, navigate]);

  async function handleResend() {
    if (resendCooldown > 0 || resending) return;
    setResending(true);
    try {
      await supabase.auth.resend({ type: 'signup', email });
      setResendOk(true);
      setResendCooldown(60);
      const t = setInterval(() => {
        setResendCooldown(c => {
          if (c <= 1) { clearInterval(t); return 0; }
          return c - 1;
        });
      }, 1_000);
    } finally {
      setResending(false);
    }
  }

  return (
    <div style={{
      minHeight:       '100vh',
      backgroundColor: 'var(--bg-base)',
      display:         'flex',
      alignItems:      'center',
      justifyContent:  'center',
      padding:         '1.5rem',
    }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Link to="/" style={{ display: 'inline-block', textDecoration: 'none' }}>
            <img src="/logo.svg" alt="Professya" style={{ height: '44px' }} />
          </Link>
        </div>

        <div className="card" style={{ textAlign: 'center' }}>
          {/* Icon */}
          <p style={{ fontSize: '3rem', margin: '0 0 0.75rem' }}>✉</p>

          <h2 style={{
            fontFamily: 'var(--font-display)',
            fontStyle:  'italic',
            fontWeight: 400,
            fontSize:   'var(--text-28)',
            color:      'var(--bg)',
            margin:     '0 0 0.5rem',
          }}>
            Confirme seu email
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-label)', margin: '0 0 1.5rem' }}>
            Enviamos um link para{' '}
            <strong style={{ color: 'var(--bg)' }}>{email}</strong>
          </p>

          {/* Steps */}
          <div style={{
            textAlign:    'left',
            background:   'var(--bg-elevated)',
            borderRadius: 'var(--radius-sm)',
            padding:      '1rem 1.25rem',
            marginBottom: '1.5rem',
            display:      'flex',
            flexDirection: 'column',
            gap:          '0.875rem',
          }}>
            {[
              ['1', 'Abra o email que acabamos de enviar'],
              ['2', 'Clique em "Confirmar Email"'],
              ['3', 'Você será redirecionado automaticamente'],
            ].map(([step, text]) => (
              <div key={step} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
                <span style={{
                  flexShrink:     0,
                  width:          '1.4rem',
                  height:         '1.4rem',
                  borderRadius:   '50%',
                  background:     'var(--petrol)',
                  display:        'flex',
                  alignItems:     'center',
                  justifyContent: 'center',
                  fontSize:       'var(--text-caption)',
                  fontWeight:     700,
                  color:          '#fff',
                }}>
                  {step}
                </span>
                <p style={{ margin: 0, fontSize: 'var(--text-label)', color: 'var(--text-secondary)' }}>{text}</p>
              </div>
            ))}
          </div>

          {resendOk && (
            <p style={{ color: 'var(--petrol)', fontSize: 'var(--text-caption)', marginBottom: '0.75rem' }}>
              ✓ Email reenviado!
            </p>
          )}

          <button
            onClick={handleResend}
            disabled={resendCooldown > 0 || resending}
            style={{
              background: 'none',
              border:     'none',
              color:      'var(--petrol)',
              cursor:     resendCooldown > 0 ? 'not-allowed' : 'pointer',
              fontSize:   'var(--text-label)',
              fontWeight: 600,
              opacity:    resendCooldown > 0 ? 0.5 : 1,
              padding:    0,
              marginBottom: '1.25rem',
            }}
          >
            {resending         ? 'Reenviando…'
             : resendCooldown > 0 ? `Reenviar em ${resendCooldown}s`
             : 'Não recebeu? Reenviar email'}
          </button>

          <div style={{
            padding:      '0.875rem 1rem',
            borderRadius: 'var(--radius-sm)',
            background:   'var(--bg-elevated)',
            border:       '1px solid var(--line-soft)',
            fontSize:     'var(--text-caption)',
            color:        'var(--text-secondary)',
            textAlign:    'left',
          }}>
            ℹ Não viu o email? Verifique a pasta de spam ou promoções. Pode levar até 2 minutos.
          </div>
        </div>

        <p style={{ textAlign: 'center', marginTop: '1.25rem', fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
          <Link to="/register" style={{ color: 'var(--petrol)' }}>← Voltar ao cadastro</Link>
        </p>
      </div>
    </div>
  );
}
